/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useCallback, useEffect } from 'react';
import { initializeLocal } from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { getAuthData, postDataToParent } from 'utils/helper';

import CssBaseline from '@mui/material/CssBaseline';

import { DupontLogger, DupontThemeProvider, DupontToastProvider, useSalesforceSSO } from 'packages/ui-components';
import { DupontShelterTheme } from 'packages/ui-styles';

import { ChatbotState } from 'store';
import { setActiveChatData } from 'store/activeChatItemSlice';
import { updateActiveChat } from 'store/chatHistorySlice';
import { Task } from 'store/chemicalStoreSlice';
import { setUserData } from 'store/userDataSlice';
import CHATBOT_APIS from 'constants/api.urls';
import { IUserDetails } from 'constants/chat.interface';
import { postRequest, setRequestHeaders } from 'services/api.service';

import ChatbotLoader from 'components/ChatbotLoader';

import 'packages/ui-styles/global.scss';

const Login = React.lazy(() => import('containers/SSOLogin'));
const Home = React.lazy(() => import('containers/ChatbotHome'));
const Logout = React.lazy(() => import('containers/SSOLogout'));
const AdminPortal = React.lazy(() => import('containers/ChatbotAdminPortal'));

function Chatbot() {
  const Logger = DupontLogger('Chatbot');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateCallback = useCallback((url: string) => navigate(url), [navigate]);

  const { activeChatItem } = useSelector((state: ChatbotState) => state);
  const { isAuthenticating, tokenData, initSSOAuth, getAuthTokens } = useSalesforceSSO({ ...getAuthData() });
  const isInitCode = window.location.search.includes('code=');

  useEffect(() => {
    if (isInitCode) {
      getAuthTokens('authorization_code');
    }
  }, [isInitCode]);

  window.onbeforeunload = () => {
    const lastVisitedURL = window.location.pathname;
    if (lastVisitedURL === '/admin-portal') sessionStorage.setItem('LAST_VISITED_URL', lastVisitedURL);
  };

  const userLoginReport = async (userInfo: IUserDetails) => {
    const { First_Name, Last_Name, UserCity, UserEmail, UserId, Role, Business_Industry } = userInfo;
    try {
      const payload = {
        first_name: First_Name,
        last_name: Last_Name,
        email_id: UserEmail,
        city: UserCity,
        user_sso_id: UserId,
        industry: Business_Industry,
        role: Role,
      };
      dispatch(setUserData({ userDetails: payload }));
      const userDetails = await postRequest(CHATBOT_APIS.reportLogin, payload);
      dispatch(setUserData({ userDetails }));

      const langRegionStr: string | null = sessionStorage.getItem('langRegion');
      let langRegion: object = {};
      if (langRegionStr) {
        langRegion = JSON.parse(langRegionStr);
      }
      dispatch(setUserData(langRegion));
    } catch (err) {
      Logger.error('err: ', err);
    }
  };

  useEffect(() => {
    const value = { activeChatId: activeChatItem?.chat_history_id || '' };
    postDataToParent(value);
  }, [activeChatItem.chat_history_id]);

  const getActiveChatData = (activeChatId: string | null) => {
    const chat_history_id = activeChatId ? (parseInt(activeChatId) >= 0 ? +activeChatId : null) : null;
    const previousChatData = {
      isLoading: Boolean(chat_history_id),
      chat_history_id,
      history: [],
      chemicals: [],
      chemicalSelectionType: '',
      isPermeation: false,
      chemicalList: [],
      chemicalClassList: [],
      industryTask: {} as Task,
      fabrics: [],
    };
    Logger.log('Authentication - getActiveChatData: ', previousChatData);
    dispatch(setActiveChatData(previousChatData));
  };

  useEffect(() => {
    //If user was loggedin and refreshed, then initiate SSO auth again
    const isUserLoggedin = sessionStorage.getItem('isUserLoggedin') === 'true';
    Logger.log('testSessionAfterLogOut', isUserLoggedin);
    if (isUserLoggedin) {
      initSSOAuth();
    }
    let lastActiveChatID = sessionStorage.getItem('activeChatId') || '';
    let loggedinUserEmail;
    let loginTokenVal;
    const readQueryParameters: any = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const appInfo = searchParams.get('appInfo');
      let langRegion;
      const storedlangRegion = sessionStorage.getItem('langRegion');
      if (storedlangRegion !== null) {
        langRegion = JSON.parse(storedlangRegion); // Outputs: anotherValue
        Logger.log('storedlangRegion ', langRegion);
        setRequestHeaders(langRegion);
      }

      if (appInfo) {
        const parsedAppInfo = JSON.parse(decodeURIComponent(appInfo));
        Logger.log('Query Params: ', parsedAppInfo);
        const { activeChatId, page, user, showWelcomeDlg, loginToken } = parsedAppInfo;
        const { aemPageurl } = page;
        loggedinUserEmail = user?.email;
        loginTokenVal = loginToken;
        let [, , , , region, lang] = aemPageurl.split('/');
        if (region === 'sg'.toLocaleLowerCase()) region = 'ap';
        langRegion = { lang, region };
        dispatch(setUserData(langRegion));
        dispatch(updateActiveChat(activeChatId));
        setRequestHeaders(langRegion);
        Logger.log('HeaderslangRegion ', langRegion);
        sessionStorage.setItem('langRegion', JSON.stringify(langRegion));
        sessionStorage.setItem('showWelcomeDlg', showWelcomeDlg);
        sessionStorage.setItem('loginToken', loginToken);
        if (activeChatId && activeChatId !== 'null') {
          lastActiveChatID = activeChatId;
        }
      }
      Logger.log('Chatbot - langRegion: ', langRegion);
      if (langRegion?.region === 'es' && langRegion?.lang === 'es') initializeLocal('eses');
      else if (langRegion?.lang === 'es') initializeLocal('esla');
      else initializeLocal(langRegion?.lang);
    };
    readQueryParameters();
    Logger.log('Authentication - loggedinUserEmail: ', loggedinUserEmail);
    // if (loggedinUserEmail && loginTokenVal) {
    if (loggedinUserEmail) {
      initSSOAuth();
      sessionStorage.setItem('activeChatId', lastActiveChatID);
    } else {
      getActiveChatData(lastActiveChatID);
    }
    navigateCallback('/home');
  }, []);

  useEffect(() => {
    Logger.log('Authentication - tokenData: ', isAuthenticating, tokenData);
    // const storedLoginToken = sessionStorage.getItem('loginToken');
    if (!isAuthenticating && tokenData) {
      //  if (storedLoginToken || process.env.REACT_APP_ENVIRONMENT === 'dev')
      sessionStorage.setItem('isUserLoggedin', 'true');
      dispatch(setUserData(tokenData));
      const lastVisitedURL = sessionStorage.getItem('LAST_VISITED_URL');
      if (lastVisitedURL) {
        Logger.log('lastVisitedURL: ', lastVisitedURL);
        navigateCallback(lastVisitedURL);
        sessionStorage.removeItem('LAST_VISITED_URL');
      }
      setRequestHeaders({ authorization: 'Bearer ' + tokenData.authToken });
      if (tokenData.userDetails) {
        userLoginReport(tokenData.userDetails);
      }

      const isUserLoggedin = sessionStorage.getItem('isUserLoggedin') === 'true';
      if (isUserLoggedin) {
        const activeChatId = sessionStorage.getItem('activeChatId');
        Logger.log('Authentication - tokenData -=> activeChatId: ', activeChatId);
        getActiveChatData(activeChatId);
        sessionStorage.setItem('activeChatId', '');
      }
    }
  }, [tokenData]);

  return (
    <DupontToastProvider>
      <DupontThemeProvider theme={DupontShelterTheme}>
        <CssBaseline />
        {isAuthenticating ? (
          <ChatbotLoader loaderClass='fixed-size-chatbot-loader' />
        ) : (
          <Suspense fallback={<ChatbotLoader loaderClass='fixed-size-chatbot-loader' />}>
            <Routes>
              <Route path='/login' element={<Login />} />
              <Route path='/home' element={<Home />} />
              <Route path='/admin-portal' element={<AdminPortal />} />
              <Route path='/logout' element={<Logout />} />
            </Routes>
          </Suspense>
        )}
      </DupontThemeProvider>
    </DupontToastProvider>
  );
}

export default Chatbot;
